/**
 * @class ProductInfo
 * @description Handles the quantity input outside the product form and sets the value to the product form for the correct add to cart submit.
 */
class ProductInfo extends HTMLElement {
  constructor() {
    super();
    this.hasRun = false;

    this.productForm = this.querySelector('product-form');
    this.productFormQuantity = this.productForm?.querySelector('input[name="quantity"]');
    this.quantityInput = this.querySelector('quantity-input');
    this.quantityInputElement = this.quantityInput?.querySelector('input[name="quantity"]');

    this.reviewStarHeader = document.querySelector('.js-reviewStarHeader');
    this.reviewCounterHeader = document.querySelector('.js-reviewCounterHeader');
    this.reviewsWidget = document.querySelector('#reviews-widget');
    this.reviewsWrapper = document.querySelector('[data-product-rating-wrapper]');

    if (this.reviewsWidget) {
      this.lang = this.reviewsWidget.dataset.lang;
      this.showProductRating();
    }

    this.quantityInputElement?.addEventListener('change', () => {
      this.handleQuantityInput();
    });

    this.observer = new MutationObserver(this.checkElements.bind(this));
    const observerOptions = {
      childList: true,
      subtree: true,
    };

    this.observer.observe(document.body, observerOptions);
    this.checkElements();

    this.initStickyAddToCart();
  }

  checkElements() {
    const reviewsPolaris = document.querySelector('#ReviewsWidget');
    const reviewsFilters = document.querySelectorAll('.R-DropUpDownMenu__inner');
    if (reviewsPolaris && reviewsFilters.length > 1 && !this.hasRun) {
      this.hasRun = true;
      this.observer.disconnect();

      setTimeout(() => {
        this.handleRatingLanguageWhenReady(reviewsPolaris, reviewsFilters);
      }, 500);
    }
  }

  initStickyAddToCart() {
    const stickyAddToCart = this.querySelector('[data-sticky-add-to-cart]');
    const stickyAddToCartGap = this.querySelector('[data-sticky-add-to-cart-replace]');
    const regularBuyButton = this.querySelector('[data-add-to-cart-wrapper]');
    let animQueue = null;
    let timeoutDuration = 300;

    const observerCartButton = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (window.innerWidth <= 1024) {
          if (entry.isIntersecting) {
            animQueue = null;
            timeoutDuration = 0;
            if (entry.boundingClientRect.top < 0) {
              Object.assign(stickyAddToCart.style, { bottom: '-100px', opacity: '0' });
              timeoutDuration = 200;
            }
            animQueue = setTimeout(() => {
              Object.assign(stickyAddToCart.style, { bottom: '0', opacity: '1' });
              stickyAddToCart.classList.remove(
                'transition-all',
                'duration-300',
                'fixed',
                'z-fixed',
                'p-4',
                'bg-sand'
              );
              stickyAddToCart.classList.add('relative');
              stickyAddToCartGap.classList.remove('block');
              stickyAddToCartGap.classList.add('hidden');
              document.body.classList.remove('chat-button-sticky');
            }, timeoutDuration);
          } else if (entry.boundingClientRect.top < 0) {
            animQueue = null;
            stickyAddToCartGap.classList.replace('hidden', 'block');
            document.body.classList.add('chat-button-sticky');
            stickyAddToCart.classList.remove('relative');
            stickyAddToCart.classList.add('fixed', 'z-fixed', 'p-4', 'bg-sand');
            Object.assign(stickyAddToCart.style, { bottom: '-100px', opacity: '0' });
            animQueue = setTimeout(() => {
              stickyAddToCart.classList.add('transition-all', 'duration-300');
              Object.assign(stickyAddToCart.style, { bottom: '0', opacity: '1' });
            }, 100);
          } else {
            stickyAddToCart.classList.add('relative');
            stickyAddToCartGap.classList.add('hidden');
          }
        }
      });
    });

    observerCartButton.observe(regularBuyButton);
  }

  handleRatingLanguageWhenReady(reviewsPolaris, reviewsFilters) {
    if (!reviewsPolaris || !reviewsFilters) {
      return;
    }

    const languageMap = {
      de: 'Deutsch',
      en: 'English',
      fr: 'Français',
      es: 'Español',
      it: 'Italiano',
    };

    reviewsFilters.forEach((filter, index) => {
      const wrapper = filter.getElementsByTagName('div')[0];
      const filterItems = wrapper.querySelectorAll('[role="button"]');

      // If going through the first filter dropdown, select the last item in the first group (best reviews)
      if (index === 0) {
        filterItems[filterItems.length - 1].click();
      } else {
        // On other filter dropdowns, try find the matching language.
        for (let i = 0; i < filterItems.length; i++) {
          const item = filterItems[i];
          const languageElement = item.querySelector('.R-flex-row > div');
          const languageText = languageElement?.textContent.trim();

          if (languageText && languageText.includes(languageMap[this.lang])) {
            setTimeout(() => {
              item.click();
            }, 300);
            break;
          }
        }
      }
    });
  }

  showProductRating() {
    if (!this.reviewCounterHeader || !this.reviewStarHeader) {
      return;
    }
    const reviewsioUrlKey = this.reviewsWidget.dataset.storeId;
    const productSkus = this.reviewsWidget.dataset.productSkus;
    const lang = this.reviewsWidget.dataset.lang;
    const skuApiUrl = `https://api.reviews.io/timeline/data?type=product_review&store=${reviewsioUrlKey}&sku=${productSkus}&enable_syndication=true&enable_avatars=true&include_subrating_breakdown=1`;
    fetch(skuApiUrl, {
      method: 'GET',
      headers: { accept: 'application/json' },
    })
      .then((response) => response.json())
      .then((data) => {
        var reviewsIoRating = parseFloat(data.stats.average_rating);
        if (reviewsIoRating > 0) {
          this.reviewsWrapper.classList.remove('hidden');
          this.reviewsWrapper.classList.add('flex');
          this.reviewCounterHeader.innerHTML = reviewsIoRating.toFixed(1);
          var ratingHtml = '';
          for (var i = 1; i <= 5; i++) {
            if (Math.ceil(reviewsIoRating) >= i) {
              ratingHtml += '★ ';
            }
          }
          this.reviewStarHeader.innerHTML = ratingHtml;
        }
      })
      .catch((error) => {
        console.error('Error fetching product ratings:', error);
      });
  }

  /**
   * handleQuantityInput
   * @description Set the quantity from the quantity input web component to the product form web component.
   */
  handleQuantityInput() {
    this.productFormQuantity.value = this.quantityInputElement.value;
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}

/**
 * @class ColorProducts
 * @extends {HTMLElement}
 */
class ColorProducts extends HTMLElement {
  // Lets watch the following attributes on the web component
  static observedAttributes = ['title', 'stock-text', 'stock-available'];

  /**
   * init
   */
  init() {
    this.snapshot = {
      title: this.getAttribute('title'),
      stockText: this.getAttribute('stock-text'),
      stockAvailable: this.getAttribute('stock-available'),
    };

    // Add addEventListener to colors
    this.colors = this.querySelectorAll('[data-color]');
    this.colors.forEach((color) => {
      color.addEventListener('mouseenter', () => {
        this.setAttribute('title', color.dataset.colorTitle);
        this.setAttribute('stock-text', color.dataset.colorStockText);
        this.setAttribute('stock-available', color.dataset.colorStockAvailable);
      });
      color.addEventListener('mouseleave', () => {
        this.setAttribute('title', this.snapshot.title);
        this.setAttribute('stock-text', this.snapshot.stockText);
        this.setAttribute('stock-available', this.snapshot.stockAvailable);
      });
      color.addEventListener('click', () => {
        this.snapshot.title = color.dataset.colorTitle;
        this.setAttribute('title', color.dataset.colorTitle);
      });
    });
  }

  /**
   * render
   * @description Render the content based on the attribute states.
   */
  render() {
    this.querySelector('[data-title]').textContent = this.getAttribute('title') || '';
    this.querySelector('[data-stock-text]').textContent = this.getAttribute('stock-text') || '';
    const stockAvailableSelector = this.querySelector('[data-stock-available]');
    stockAvailableSelector.classList.remove('text-red', 'text-green');
    this.getAttribute('stock-available') === 'true'
      ? stockAvailableSelector.classList.add('text-green')
      : stockAvailableSelector.classList.add('text-red');
  }

  /**
   * connectedCallback
   */
  connectedCallback() {
    if (this.isConnected) {
      this.init();
      this.render();
    }
  }

  /**
   * attributeChangedCallback
   */
  attributeChangedCallback() {
    this.render();
  }
}

if (!customElements.get('color-products')) {
  customElements.define('color-products', ColorProducts);
}
